import type { TOC } from '@ember/component/template-only';
import { PhCheckCircle, PhHourglass, PhClock } from 'ember-phosphor-icons';
import { eq } from 'tio-ui/utilities';
import { Text } from 'tio-ui/components/typography';
import Chip from 'tio-reimagined/components/chip';
import { formatCurrency, formatMonthYear } from 'tio-reimagined/format';

/**
 * PaymentStatusCard — Displays a last or next payment with amount, date, and status
 *
 * Used on the reimagined finance/history page to show at-a-glance payment info.
 *
 * @example
 * ```gts
 * <PaymentStatusCard
 *   @title="Last Payment"
 *   @amount={{125}}
 *   @date="2026-01-15"
 *   @status="sent"
 *   @theme="violet"
 * />
 * ```
 */

type PaymentStatus = 'sent' | 'in_progress' | 'pending';
type Theme = 'violet' | 'blue' | 'emerald' | 'amber';

interface PaymentStatusCardSignature {
  Args: {
    /** Card title (e.g. "Last Payment", "Next Payment") */
    title: string;
    /** Payment amount in dollars */
    amount: number;
    /** Date string (ISO or displayable) */
    date?: string | null;
    /** Payment status */
    status: PaymentStatus;
    /** Color theme for accent */
    theme?: Theme;
  };
  Element: HTMLDivElement;
}

function statusLabel(status: PaymentStatus): string {
  switch (status) {
    case 'sent':
      return 'Payment Sent';
    case 'in_progress':
      return 'In Progress';
    case 'pending':
      return 'Pending';
  }
}

function statusIntent(status: PaymentStatus): 'success' | 'primary' | 'default' {
  switch (status) {
    case 'sent':
      return 'success';
    case 'in_progress':
      return 'primary';
    case 'pending':
      return 'default';
  }
}

const THEME_CLASSES: Record<Theme, { border: string; valueTxt: string; iconBg: string }> = {
  violet: {
    border: 'border-violet-200',
    valueTxt: 'text-violet-700',
    iconBg: 'bg-violet-100 text-violet-600',
  },
  blue: {
    border: 'border-blue-200',
    valueTxt: 'text-blue-700',
    iconBg: 'bg-blue-100 text-blue-600',
  },
  emerald: {
    border: 'border-emerald-200',
    valueTxt: 'text-emerald-700',
    iconBg: 'bg-emerald-100 text-emerald-600',
  },
  amber: {
    border: 'border-amber-200',
    valueTxt: 'text-amber-700',
    iconBg: 'bg-amber-100 text-amber-600',
  },
};

const PaymentStatusCard: TOC<PaymentStatusCardSignature> = <template>
  {{! template-lint-disable no-bare-strings }}
  {{#let (if @theme (get THEME_CLASSES @theme) THEME_CLASSES.violet) as |theme|}}
    <div
      class="bg-white rounded-xl p-5 shadow-sm border {{theme.border}} flex flex-col gap-3"
      ...attributes
    >
      <div class="flex items-center justify-between">
        <Text class="text-sm font-medium text-gray-500">{{@title}}</Text>
        {{#if @date}}
          <Text class="text-sm text-gray-400">{{formatMonthYear @date}}</Text>
        {{/if}}
      </div>

      <div class="flex items-center gap-3">
        <div class="p-2 rounded-lg {{theme.iconBg}}">
          {{#if (eq @status "sent")}}
            <PhCheckCircle @size={{24}} />
          {{else if (eq @status "in_progress")}}
            <PhHourglass @size={{24}} />
          {{else}}
            <PhClock @size={{24}} />
          {{/if}}
        </div>
        <div class="text-2xl font-bold {{theme.valueTxt}}">
          {{formatCurrency @amount}}
        </div>
      </div>

      <div>
        <Chip @intent={{statusIntent @status}} @size="sm">
          {{statusLabel @status}}
        </Chip>
      </div>
    </div>
  {{/let}}
</template>;

// Helper to access record properties in templates
function get<T>(obj: Record<string, T>, key: string): T | undefined {
  return obj[key];
}

export default PaymentStatusCard;
